import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useTheme } from 'react-jss'
import {
    Root,
    Overlay,
    Content,
    Description,
    Portal,
} from '@radix-ui/react-dialog'
import { useStyles } from '../../ui/Dialog/Dialog'
import { colors } from '../../../theme/palette'
import { IkonSammenlaeg } from '../../../assets/icons'
import { Case } from '../../../models/Case'
import Autocomplete from '../../ui/Autocomplete/Autocomplete'
import { fetchCasesAtom } from '../../../store/filter/filterCasesAtom'
import { L } from '../../providers/Lokalise'
import { Button } from '../../ui/Button/Button'
import { Typography } from '../../ui/Typography/Typography'
import { tryAttachCase, attachCase } from '../../../api/salary'
import Close from '../../ui/Dialog/Close'
import { useErrorDialog } from '../../../hooks/useErrorDialog'
import { ErrorDisplay } from './Contact/ContactDialog'
import { formatDate } from '../../../utils/format'
import Title from '../../ui/Dialog/Title'

interface AttachToAnotherCaseProps {
    currentCase: Case
    onSuccess?: () => void
    onCancel?: () => void
    openCases?: Case[]
    redirectToParentCase?: boolean
}

type CustomErrorsProps = {
    caseId?: {
        message: string
    }
}

const AttachToAnotherCaseContent = ({
    currentCase,
    onSuccess,
    onCancel,
    openCases,
    redirectToParentCase,
}: AttachToAnotherCaseProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState(false)
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white
    const [, fetchCases] = useAtom(fetchCasesAtom)
    const [selectedCase, setSelectedCase] = useState<Case | undefined>(
        undefined
    )
    const [step, setStep] = useState<'select' | 'confirm'>('select')
    const [finalCase, setFinalCase] = useState<Case | undefined>(undefined)
    const [customErrorMessage, setCustomErrorMessage] = useState<
        CustomErrorsProps | undefined
    >(undefined)
    const [attachCaseData, setAttachCaseData] = useState<
        | {
              caseId: string
              caseRegTime: string
              otherCaseId: string
              otherCaseRegTime: string
          }
        | undefined
    >(undefined)
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const navigate = useNavigate()
    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    const handleSelect = (value: string): void => {
        // Add your logic here to handle the selected case
        const selected = openCases?.find((c) => c.caseId === value)

        if (selected) {
            setCustomErrorMessage(undefined)
            setSelectedCase(selected)
        }
    }

    const handleTryAttach = async (): Promise<void> => {
        const customError: CustomErrorsProps = {}

        if (!selectedCase) {
            customError.caseId = {
                message: 'Sag er påkrævet',
            }
            setCustomErrorMessage(customError)
            return
        }

        if (selectedCase) {
            setIsLoading(true)
            const response = await tryAttachCase({
                caseId: currentCase.caseId,
                caseRegTime: currentCase.caseRegistrationDate,
                otherCaseId: selectedCase.caseId,
                otherCaseRegTime: selectedCase.caseRegistrationDate,
            })

            if (response.error) {
                showError(<ErrorDisplay errors={response.error.errors} />)
                setIsLoading(false)
            }

            if (response) {
                if (response.sagsId === selectedCase.caseId) {
                    setFinalCase(selectedCase)
                    setAttachCaseData({
                        caseId: selectedCase.caseId,
                        caseRegTime: selectedCase.caseRegistrationDate,
                        otherCaseId: currentCase.caseId,
                        otherCaseRegTime: currentCase.caseRegistrationDate,
                    })
                    setStep('confirm')
                } else if (response.sagsId === currentCase.caseId) {
                    setFinalCase(currentCase)
                    setAttachCaseData({
                        caseId: currentCase.caseId,
                        caseRegTime: currentCase.caseRegistrationDate,
                        otherCaseId: selectedCase.caseId,
                        otherCaseRegTime: selectedCase.caseRegistrationDate,
                    })
                    setStep('confirm')
                }
            }
            setIsLoading(false)
        }
    }

    const handleGoBack = (): void => {
        setStep('select')
    }

    const handleAttach = async (): Promise<void> => {
        setIsLoading(true)

        const response = await attachCase({
            caseId: attachCaseData?.caseId,
            caseRegTime: attachCaseData?.caseRegTime,
            otherCaseId: attachCaseData?.otherCaseId,
            otherCaseRegTime: attachCaseData?.otherCaseRegTime,
        })

        if (response.error) {
            showError(<ErrorDisplay errors={response.error.errors} />)
            setIsLoading(false)
            return
        }

        if (response) {
            onSuccess?.()
            if (redirectToParentCase) {
                // window.location.href = `/mine-lonindplaceringer/sag/${currentCase.caseId}`
                navigate(`/mine-lonindplaceringer/sag/${finalCase?.caseId}`)
            }
        }
        setIsLoading(false)
    }

    const renderConfirmStep = (): JSX.Element => {
        return (
            <>
                <Description style={{ marginBottom: '2rem' }}>
                    {L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_BESKRIVELSE')}
                </Description>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem',
                        border: `1px solid ${colors.green}`,
                        padding: '1rem',
                        borderRadius: '0.5rem',
                    }}
                >
                    <Typography size="XS">
                        {finalCase?.newlyHired.name}
                    </Typography>
                    <Typography size="XXXS" color={colors.gray900}>
                        <span
                            style={{
                                marginRight: '1rem',
                            }}
                        >
                            {finalCase?.caseNumber}
                        </span>
                        <span style={{ marginRight: '.6rem' }}>
                            {`${L(
                                'LØN_SAMMENLÆG_SAG_AUTOCOMPLETE_MODTAGET_DATO'
                            )} ${formatDate(
                                new Date(finalCase?.receivedDate)
                            )}`}
                        </span>
                        <span>
                            {`${L(
                                'LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_STATUS'
                            )}: ${finalCase?.caseStatus.statusNavn}`}
                        </span>
                    </Typography>
                    <Typography size="XS">
                        {finalCase?.newlyHired.workplace?.navn}
                        {', '}
                        {finalCase?.newlyHired.position?.navn}
                    </Typography>
                </div>
                <div
                    style={{
                        marginTop: '3rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        minWidth: '200px',
                        maxWidth: '100%',
                        width: '100%',
                    }}
                >
                    <Button
                        size="xlarge"
                        type="secondary"
                        onClick={handleGoBack}
                        style={{
                            flex: 1,
                        }}
                    >
                        {L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_FORRIGE_KNAP')}
                    </Button>
                    <Button
                        size="xlarge"
                        type="primary"
                        disabled={!selectedCase}
                        loading={isLoading}
                        style={{
                            flex: 1,
                        }}
                        onClick={handleAttach}
                    >
                        {L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_KNAP')}
                    </Button>
                </div>
                <ErrorDialogComponent />
            </>
        )
    }

    const renderSelectStep = (): JSX.Element => {
        return (
            <>
                <Description style={{ marginBottom: '2.5rem' }}>
                    {L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_BESKRIVELSE_2')}
                </Description>
                <Autocomplete
                    label={L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_LABEL_1')}
                    required={false}
                    suggestions={[]}
                    onSelect={handleSelect}
                    cases={(openCases as Case[])?.filter(
                        (c) => c.caseId !== currentCase.caseId
                    )}
                    defaultSuggestion={selectedCase?.caseId ?? undefined}
                    hasError={!!customErrorMessage?.caseId}
                />
                <div
                    style={{
                        marginTop: '3rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        justifyContent: 'space-between',
                        minWidth: '200px',
                        maxWidth: '100%',
                    }}
                >
                    <Button
                        size="xlarge"
                        type="primary"
                        disabled={isLoading}
                        loading={isLoading}
                        style={{
                            flex: 1,
                        }}
                        onClick={handleTryAttach}
                    >
                        {L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_NAESTE_KNAP')}
                    </Button>
                </div>
            </>
        )
    }
    return (
        <div className={`${classes.container} withIcon`}>
            <div className={`${classes.circleIcon} withIcon`}>
                <IkonSammenlaeg width="46px" fill={colors.green500} />
            </div>
            <Title
                style={{ marginBottom: '1.5rem' }}
                title={L('LØN_OVERSIGT_SAMMENLAG_MED_ANDEN_SAG_TITEL')}
            />
            <Close handleClose={onCancel} />
            {step === 'select' && renderSelectStep()}
            {step === 'confirm' && renderConfirmStep()}
            <ErrorDialogComponent />
        </div>
    )
}

const AttachToAnotherCase: React.FC<AttachToAnotherCaseProps> = ({
    currentCase,
    onSuccess,
    onCancel,
    openCases,
    redirectToParentCase,
}): JSX.Element => {
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white

    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    return (
        <Root defaultOpen>
            <Portal>
                <Overlay className={classes.overlay} />
                <Content className={`${classes.overlayContainer}`}>
                    <AttachToAnotherCaseContent
                        currentCase={currentCase}
                        onSuccess={onSuccess}
                        onCancel={onCancel}
                        openCases={openCases}
                        redirectToParentCase={redirectToParentCase}
                    />
                </Content>
            </Portal>
        </Root>
    )
}

export { AttachToAnotherCase }
